/**
 * "Message a vendor about their punch items" (2026-09-22).
 *
 * Pick a vendor, tick the items to send, send. The message goes through the
 * device's own share sheet — the same dispatcher as every other message in
 * this app since Phase 19 — and is then recorded in Notifications so it can be
 * looked at, and sent again, later.
 *
 * ⚠️ THE GESTURE TRAP (see share-sheet.ts): navigator.share() must be called
 * synchronously inside the click handler. So the message text is composed at
 * render time and rebuilt on every tick, never fetched — and the recording,
 * which is a network call, happens AFTER the share resolves.
 */

import { esc } from '@/lib/dom';
import { shareOrFallback, countsAsShared } from '@/lib/share-sheet';
import { recordSentMessage } from '@/lib/data/notifications';
import { punchMessage, filterPunch, sortPunch, PUNCH_NO_ROOM } from '@/lib/punch';
import { toastError } from '@/lib/toast';
import type { RoomRow, PunchItemRow, DesignerContactRow, ProjectRow } from '@/types/database';

export interface PunchMessageOptions {
  project: Pick<ProjectRow, 'id' | 'name'>;
  rooms: RoomRow[];
  contacts: DesignerContactRow[];
  punch: PunchItemRow[];
  preselectContactId?: string | null;
  onSent: (vendorName: string) => void;
}

export function openPunchMessage(opts: PunchMessageOptions): HTMLElement {
  const { project, rooms, contacts, punch } = opts;
  /**
   * Two callers, two right answers (0048).
   *
   * In the MESSAGE, an item with no room carries no location at all — texting
   * a contractor "No room" tells them nothing a missing room didn't already.
   * In the PICKER the designer is looking at, "No room" is exactly what they
   * need to see to know which item they're ticking.
   */
  const roomForMessage = (id: string | null): string =>
    id === null ? '' : rooms.find((r) => r.id === id)?.name ?? '';
  const roomForPicker = (id: string | null): string =>
    id === null ? PUNCH_NO_ROOM : rooms.find((r) => r.id === id)?.name ?? '';

  // Only vendors with open items: offering someone with nothing outstanding
  // leads to an empty list and a dead Send button.
  const withOpenWork = contacts.filter((c) =>
    punch.some((p) => !p.complete && p.designer_contact_id === c.id));

  let contactId = opts.preselectContactId && withOpenWork.some((c) => c.id === opts.preselectContactId)
    ? opts.preselectContactId
    : withOpenWork[0]?.id ?? '';
  // null = "not chosen yet", which is not the same as "chosen nothing".
  // Conflating them means unticking the last item silently re-ticks them all.
  let chosen: Set<string> | null = null;

  const overlay = document.createElement('div');
  overlay.className = 'modal';
  const close = (): void => {
    overlay.remove();
    document.removeEventListener('keydown', onKey);
  };
  const onKey = (e: KeyboardEvent): void => { if (e.key === 'Escape') close(); };

  const itemsFor = (id: string): PunchItemRow[] =>
    sortPunch(filterPunch(punch, { room: 'all', vendor: id, show: 'open' }), rooms);

  const messageFor = (): string => {
    const contact = contacts.find((c) => c.id === contactId) ?? null;
    return punchMessage({
      vendorName: contact?.name ?? null,
      projectName: project.name,
      items: itemsFor(contactId)
        .filter((p) => chosen?.has(p.id) ?? false)
        .map((p) => ({ description: p.description, due_date: p.due_date, roomName: roomForMessage(p.room_id) })),
    });
  };

  const draw = (): void => {
    const items = itemsFor(contactId);
    // Everything outstanding to start with; after that, whatever is still
    // ticked and still on this vendor's list.
    const picked = chosen;
    const ticked = picked === null
      ? new Set(items.map((p) => p.id))
      : new Set([...picked].filter((id) => items.some((p) => p.id === id)));
    chosen = ticked;

    overlay.innerHTML = withOpenWork.length === 0 ? `
      <div class="modal-card" role="dialog" aria-label="Message a vendor">
        <h2>Message a vendor</h2>
        <p class="muted">No open punch items are assigned to anyone yet. Open an item and set
           “Vendor assigned to”, then come back.</p>
        <div class="modal-actions"><span style="flex:1"></span>
          <button type="button" class="btn-link" data-do="close">Close</button></div>
      </div>` : `
      <div class="modal-card punch-message" role="dialog" aria-label="Message a vendor">
        <h2>Message a vendor</h2>
        <label class="field">
          <span>Vendor</span>
          <select data-msg-contact>
            ${withOpenWork.map((c) => `<option value="${esc(c.id)}" ${c.id === contactId ? 'selected' : ''}>${
              esc(c.company ? `${c.name} — ${c.company}` : c.name)}</option>`).join('')}
          </select>
        </label>

        <div class="punch-message-items">
          ${items.map((p) => `
            <label class="punch-message-item">
              <input type="checkbox" data-msg-item="${esc(p.id)}" ${ticked.has(p.id) ? 'checked' : ''} />
              <span>
                ${esc(p.description)}
                <span class="muted small">${esc([roomForPicker(p.room_id), p.due_date ? `due ${p.due_date}` : '']
                  .filter(Boolean).join(' · '))}</span>
              </span>
            </label>`).join('')}
        </div>

        <div class="field">
          <span>Message</span>
          <pre class="punch-message-preview" data-msg-preview>${esc(messageFor())}</pre>
        </div>
        ${contacts.find((c) => c.id === contactId)?.phone
          ? ''
          : `<p class="muted small">No phone on file for them — your share sheet will ask who to send to.</p>`}

        <div class="modal-error" hidden></div>
        <div class="modal-actions">
          <span style="flex:1"></span>
          <button type="button" class="btn-link" data-do="close">Cancel</button>
          <button type="button" class="btn-primary" data-do="send" ${ticked.size === 0 ? 'disabled' : ''}>Send</button>
        </div>
      </div>`;
    wire();
  };

  function wire(): void {
    overlay.querySelectorAll<HTMLSelectElement>('[data-msg-contact]').forEach((sel) => {
      sel.addEventListener('change', () => { contactId = sel.value; chosen = null; draw(); });
    });
    overlay.querySelectorAll<HTMLInputElement>('[data-msg-item]').forEach((cb) => {
      cb.addEventListener('change', () => {
        const id = cb.dataset.msgItem!;
        if (cb.checked) chosen!.add(id); else chosen!.delete(id);
        // Redraw rather than patching: the preview IS the message, and a stale
        // preview beside a Send button is the one thing that must not happen.
        draw();
      });
    });
    overlay.querySelectorAll<HTMLButtonElement>('[data-do="close"]').forEach((b) => b.addEventListener('click', close));
    overlay.querySelectorAll<HTMLButtonElement>('[data-do="send"]').forEach((btn) => {
      btn.addEventListener('click', async () => {
        const contact = contacts.find((c) => c.id === contactId) ?? null;
        const text = messageFor();
        // No await before this call — see the header.
        const outcome = await shareOrFallback({ title: `${project.name} punch list`, text, phone: contact?.phone });
        if (!countsAsShared(outcome)) {
          if (outcome === 'copied') toastError('Copied the message — paste it into your messaging app.');
          else if (outcome === 'failed') toastError('Couldn’t open a way to send that message.');
          return;   // dismissed: they changed their mind, record nothing
        }
        try {
          await recordSentMessage({
            projectId: project.id, contactId, kind: 'punch_list', text, phone: contact?.phone ?? null,
          });
        } catch {
          // The message has already gone. A failed record is worth less than
          // an error dialog claiming the send failed.
        }
        close();
        opts.onSent(contact?.name ?? 'the vendor');
      });
    });
  }

  overlay.addEventListener('click', (e) => { if (e.target === overlay) close(); });
  draw();
  document.body.append(overlay);
  document.addEventListener('keydown', onKey);
  return overlay;
}
